import {
	type aliasType,
	type groupType,
	type tenantType,
	type userType,
} from '../analyticsWebTypes';
import { type validationMode } from '../eventBuilder';
import { type eventType, type ResilienceMechanism } from '../index';
import { type StoreType } from '../resiliencedb';
import type { XIDItem } from '../xid';

export type PackagedEvent = {
	headers: {
		[key: string]: string;
	};
	msg: SegmentEvent;
	url: string;
};

// Segment event types copied from our backend
// https://bitbucket.org/atlassian/analytics-service/src/master/api/src/main/java/com/atlassian/dataservices/analytics/service/api/segment/

export type SegmentBatchDef = {
	batch: SegmentEvent[];
	context?: SegmentContextDef;
	metadata?: LibraryMetadataDef;
	sentAt: string; // ISO format
};

export type SegmentContextDef = {
	active?: boolean;
	app?: SegmentAppDef;
	// Ignoring campaign as its not used by the backend
	// Ignoring device as its derived from useragent
	// Ignoring ip since the request provides it
	library?: SegmentLibraryDef;
	locale?: string;
	// Ignoring location since this is determined by ip
	// Ignoring network as its not provided by AWC,
	// Ignoring os as its derived from the useragent
	// This is added by segment but then BeforeSend integration removes it
	// Ignoring page as it was always removed by old Segment plugin.
	// Ignoring referrer as it is not used by the backend
	screen?: SegmentScreenDef;
	// Ignoring timezone as its derived from the ip
	userAgent?: string;
	validationMode?: validationMode;
};

export type SegmentAppDef = {
	build?: string;
	name?: string;
	namespace?: string;
	version?: string;
};

export type SegmentLibraryDef = {
	metadata?: LibraryMetadataDef;
	name: 'analytics.js';
	version: string;
};

export class LastError {
	public reason: string;
	public message: string | null;
	public statusCode: number | null;

	constructor(reason: string, error: any | null) {
		this.reason = reason;
		this.message = error instanceof Error ? error.message : null;
		this.statusCode = error instanceof FetchError ? error.statusCode : null;
	}
}

export type LibraryMetadataDef = {
	// Localstorage specific metrics
	duplicateEventCount?: number;
	eventCount?: number;
	failedReclaims?: number;
	fullReclaims?: number;
	httpRetryCount?: number;
	isUsingFallbackUrl?: boolean | null;
	itemsDiscardedByOverflow?: number;
	itemsDiscardedByRetry?: number;
	lastSendEventsError?: LastError | null;

	lastSendEventsErrorReason?: string | null;
	lastUrlFallbackError?: LastError | null;
	localstorageDuplicateCount?: number;
	localstorageQueuesPurged?: number;
	metadataClientMetrics?: MetadataClientMetrics | null;
	partialReclaims?: number;

	// Properties object round-tripped from the metadata endpoint to the backend service.
	// This should be treated as an opaque object.
	props?: any | null;

	resilienceMechanism?: ResilienceMechanism | StoreType;
};

export type SegmentScreenDef = {
	density?: number;
	height?: number;
	width?: number;
};

export enum SegmentEventTypes {
	IDENTIFY = 'identify',
	PAGE = 'page',
	// Dont need Screen as page and screen are the same and we only use page
	TRACK = 'track',
}

export type SegmentMetadataDef = {
	// Ignoring bundled as this is unused by the backend
	// Ignoring unbundled as this is unused by the backend
	failedAttempts?: number;
};

export type BaseSegmentEvent = {
	_metadata?: SegmentMetadataDef;
	anonymousId?: string;
	context?: SegmentContextDef;
	integrations?: { [name: string]: boolean };
	messageId: string;
	sentAt?: string; // ISO format
	timestamp: string; // ISO format
	type: SegmentEventTypes;
	userId?: string | null;
	version?: string;
};

export type SegmentIdentifyEventDef = BaseSegmentEvent & {
	traits: SegmentIdentifyEventTraitsDef;
	type: SegmentEventTypes.IDENTIFY;
};

export type SegmentIdentifyEventTraitsDef = BaseXidContainer & {
	entityId: string;
	entityTraits?: { [key: string]: unknown };
	entityType: string;
	// Internal use for checking transformation quality
	transformUUID?: string;

	userIdType: string;
};

export type SegmentScreenEventDef = BaseSegmentEvent & {
	// Ignoring channel as its unused by backend
	name?: string;
	properties: SegmentProperties;
	type: SegmentEventTypes.PAGE;
};

export type SegmentTrackEventDef = BaseSegmentEvent & {
	event: string;
	properties: SegmentTrackPropertiesDef;
	type: SegmentEventTypes.TRACK;
};

export type SegmentEvent = SegmentTrackEventDef | SegmentScreenEventDef | SegmentIdentifyEventDef;

export type SegmentProperties = BaseXidContainer & {
	aliases?: { [key in aliasType]?: string };
	attributes?: { [key: string]: unknown };
	containers?: { [containerKey: string]: SegmentContainerObjectDef };
	env: string;
	groups?: { [key in groupType]?: string };
	nonPrivacySafeAttributes?: { [key: string]: unknown };
	orgId?: string;
	origin: string;
	pageLoadId?: string;
	platform: string;
	product: string;
	sessionId?: string;
	subproduct?: string;
	tabId?: string;
	tags?: string[];
	taskSessions?: { [key: string]: unknown };
	tenantId?: string;
	tenantIdType?: tenantType;
	userIdType?: userType;
	version?: string;
	workspaceId?: string;
};

export type SegmentTrackPropertiesDef = SegmentProperties & {
	action: string;
	actionSubject: string;
	actionSubjectId?: string;
	containerId?: string;
	containerType?: string;
	datacenter?: string;
	eventType: eventType.OPERATIONAL | eventType.TRACK | eventType.UI;

	objectId?: string;
	objectType?: string;
	source: string;
};

export type SegmentContainerObjectDef = {
	id: string;
	type?: string;
};

export type BaseXidContainer = {
	xid?: XIDItem[];
};

export enum CrossDomainIdentifierType {
	XC = 'xc',
	UID = 'uid',
}

export enum CrossDomainIdentifierState {
	NEW = 'NEW',
	CHANGED = 'CHANGED',
	EXISTING = 'EXISTING',
	TIMEOUT = 'TIMEOUT',
	ERROR = 'ERROR',
	UNAVAILABLE = 'UNAVAILABLE',
	MALFORMED = 'MALFORMED',
	UNKNOWN = 'UNKNOWN',
}

export type MetadataClientMetrics = {
	host?: string;
	lastErrorReason?: string | null;
	lastRefreshRequestCount?: number;
	status: string;
};

export class MetadataResponse {
	constructor(props: any | null, url: string | null) {
		this.props = props;
		this.url = url;
	}

	props: any | null;
	url: string | null;

	public static fromJson(json: any): MetadataResponse {
		let isString = (value: any) => typeof value === 'string' || value instanceof String;
		const url = isString(json?.url) ? json.url.trim() : '';
		return new MetadataResponse(json?.props || null, url.startsWith('http') ? url : null);
	}
}

export class FetchError extends Error {
	public statusCode: number;
	public statusText: string;

	constructor(message: string, statusCode: number, statusText: string) {
		super(message);
		this.statusCode = statusCode;
		this.statusText = statusText;
	}
}

export const ALLOW_LISTED_FETCH_ERROR_NAMES: string[] = ['FetchError', 'AbortError', 'TypeError'];
